export type ValidationRule<T> = {
  condition: (value: T) => boolean;
  message: string;
};

export type ValidationSchema<T> = {
  [K in keyof T]?: ValidationRule<T[K]>[];
};

export type ValidationErrors<T> = Partial<Record<keyof T, string>>;

export const validate = <T extends object>(
  values: T,
  schema: ValidationSchema<T>
): ValidationErrors<T> => {
  const errors: ValidationErrors<T> = {};

  for (const key in schema) {
    const typedKey = key as keyof T;
    const rules = schema[typedKey];
    if (!rules) continue;

    for (const rule of rules) {
      if (rule.condition(values[typedKey])) {
        errors[typedKey] = rule.message;
        break;
      }
    }
  }

  return errors;
};

// Common validation rules
export const required = (message = "This field is required."): ValidationRule<string> => ({
  condition: (v) => !v?.trim(),
  message,
});

export const minItems = <T>(min: number, message?: string): ValidationRule<T[]> => ({
  condition: (v) => (v?.length ?? 0) < min,
  message: message ?? `Please provide at least ${min} items.`,
});

export const hasErrors = <T extends object>(errors: ValidationErrors<T>): boolean =>
  Object.values(errors).some(Boolean);
